import React, { useEffect, useState } from 'react';
import { Link, withPrefix } from 'gatsby';
import { addLinkTestId } from '../../helpers/addTestId';
import uniqueId from '../../helpers/uniqueId';
import { Link as ContentfulLink } from './types/Navigation.types';
import { MainLinksWrapper, BrandLogoWrapper } from './styles/MainLinks.styles';
import { ReactComponent as VodafoneNoMargin } from '../../images/icon/vodafone-nomargin.svg';
import { css } from 'styled-components';
import { Icon } from '@legacy-vfuk/core-icon';
import { Heading } from '@legacy-vfuk/core-heading';
import DrawerNavigation from './DrawerNavigation';
import { addSlashIfMissing } from '../../helpers/navigationHelpers';

const MainLinks = ({
  brandLink,
  mainLinks,
  topLevelLinks,
  languageLink,
  pageTitle,
  secondLanguageUrlPrefix,
  isSecondLanguageOnUrl,
  pageBuilderSlugList,
}: {
  brandLink: string;
  mainLinks: ContentfulLink[];
  topLevelLinks: ContentfulLink[];
  languageLink: ContentfulLink;
  pageTitle: string;
  secondLanguageUrlPrefix: string | undefined;
  isSecondLanguageOnUrl: boolean | undefined;
  pageBuilderSlugList: string[];
}) => {
  const [currentUrl, setCurrentUrl] = useState<string>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    setCurrentUrl(window.location.pathname);
  }, []);

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.classList.add('disable-scrolling');
      return;
    }

    document.body.classList.remove('disable-scrolling');
  }, [isDrawerOpen]);

  const getCurrentlyActiveLink = (
    mainLinks: ContentfulLink[]
  ): ContentfulLink | undefined => {
    if (!currentUrl) {
      return;
    }

    return mainLinks.find(mainLink => {
      const prefixedMainLinkUrl = withPrefix(mainLink.url);
      const homeUrl = withPrefix(
        isSecondLanguageOnUrl ? '/' + secondLanguageUrlPrefix : '/'
      );

      const isMainLinkHomeUrl = prefixedMainLinkUrl === homeUrl;

      const isCurrentUrlHomeUrl = currentUrl === homeUrl;
      const isIncludedInCurrentUrl = currentUrl.includes(prefixedMainLinkUrl);

      if (isCurrentUrlHomeUrl) {
        return isMainLinkHomeUrl;
      }

      if (!isMainLinkHomeUrl) {
        return isIncludedInCurrentUrl;
      }

      return false;
    });
  };

  return (
    <>
      <MainLinksWrapper
        id="sticky-top-header"
        activeLinkId={getCurrentlyActiveLink(mainLinks)?.contentful_id}
      >
        <div>
          <BrandLogoWrapper
            id="logo"
            href={brandLink}
            aria-label="Vodafone-logo"
            title={'vodafone-icon'}
            data-analytics-element={uniqueId('vodafone-icon')}
            data-analytics-context={pageTitle}
            data-analytics-category={'Link'}
            data-testid={'vodafone-icon'}
          >
            <VodafoneNoMargin />
          </BrandLogoWrapper>

          <nav>
            <ul className="main-nav-items">
              {mainLinks.map(mainLink => (
                <li key={mainLink.contentful_id}>
                  <Link
                    to={addSlashIfMissing(mainLink.url, {
                      missingFrom: 'start',
                    })}
                    id={mainLink.contentful_id}
                    title={mainLink.name}
                    data-analytics-element={uniqueId(mainLink.name)}
                    data-analytics-context={pageTitle}
                    data-analytics-category="Link"
                    data-testid={addLinkTestId('navLink', mainLink.name)}
                  >
                    {mainLink.name}
                  </Link>
                </li>
              ))}
            </ul>

            <ul className="mobile-nav-items">
              <li>
                <Heading size={1} level={1}>
                  V-Hub
                </Heading>
              </li>
              <li>
                <label
                  htmlFor="drawer-navigation"
                  css={css`
                    position: relative;
                    cursor: pointer;
                  `}
                >
                  <input
                    type="checkbox"
                    id="drawer-navigation"
                    checked={isDrawerOpen}
                    onChange={() => {
                      setIsDrawerOpen(currentValue => !currentValue);
                    }}
                    css={css`
                      position: absolute;
                      inset: 0;
                      display: none;
                    `}
                  />
                  <Icon name="menu" size={4} />
                </label>
              </li>
            </ul>
          </nav>
        </div>
      </MainLinksWrapper>

      <DrawerNavigation
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        getCurrentlyActiveLink={getCurrentlyActiveLink}
        mainLinks={mainLinks}
        topLevelLinks={topLevelLinks}
        languageLink={languageLink}
        pageTitle={pageTitle}
        secondLanguageUrlPrefix={secondLanguageUrlPrefix}
        isSecondLanguageOnUrl={isSecondLanguageOnUrl}
        pageBuilderSlugList={pageBuilderSlugList}
      />
    </>
  );
};

export default MainLinks;
